import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getErrorMessage } from '../../api/errors';
import { preOrdersService } from '../../services/preOrders/preOrdersService';
import type { AdminPreOrder, PreOrderStatus } from '../../types';
import { getProductUrl } from '../../utils/product';
import { PRE_ORDER_STATUS_LABELS } from '../../utils/preOrders';

type StatusFilter = 'all' | PreOrderStatus;

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('tr-TR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function AdminPreOrders() {
  const [preOrders, setPreOrders] = useState<AdminPreOrder[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('pending');
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [adminNotes, setAdminNotes] = useState<Record<string, string>>({});

  const loadPreOrders = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const data = await preOrdersService.fetchAdminPreOrders(
        statusFilter === 'all' ? undefined : statusFilter,
      );
      setPreOrders(data);
    } catch (err) {
      setError(getErrorMessage(err, 'Ön siparişler yüklenemedi.'));
    } finally {
      setIsLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    void loadPreOrders();
  }, [loadPreOrders]);

  const handleUpdate = async (id: string, status: 'fulfilled' | 'cancelled') => {
    const actionLabel = status === 'fulfilled' ? 'tamamlandı olarak işaretlemek' : 'iptal etmek';
    const confirmed = confirm(`Bu ön siparişi ${actionLabel} istediğinize emin misiniz?`);
    if (!confirmed) return;

    setUpdatingId(id);
    setError('');
    try {
      await preOrdersService.updateAdminPreOrder(id, status, adminNotes[id]?.trim() || null);
      await loadPreOrders();
    } catch (err) {
      setError(getErrorMessage(err, 'Ön sipariş güncellenemedi.'));
    } finally {
      setUpdatingId(null);
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div>
          <h1>Ön Siparişler</h1>
          <p>Stokta olmayan ürünler için müşteri talepleri</p>
        </div>
        <button type="button" className="btn btn-outline" onClick={() => void loadPreOrders()}>
          Yenile
        </button>
      </div>

      <div className="admin-order-filters">
        <label htmlFor="preOrderStatusFilter">Durum</label>
        <select
          id="preOrderStatusFilter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
        >
          <option value="all">Tümü</option>
          <option value="pending">Bekliyor</option>
          <option value="fulfilled">Tamamlandı</option>
          <option value="cancelled">İptal edildi</option>
        </select>
        {statusFilter !== 'all' && (
          <span className="admin-order-filter-count">{preOrders.length} sonuç</span>
        )}
      </div>

      {error && (
        <div className="auth-error">
          {error}
          <button type="button" className="btn btn-outline btn-sm" onClick={() => void loadPreOrders()}>
            Tekrar Dene
          </button>
        </div>
      )}

      {isLoading ? (
        <p>Yükleniyor...</p>
      ) : preOrders.length === 0 ? (
        <p className="admin-empty">Kayıt bulunamadı.</p>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table admin-pre-orders-table">
            <thead>
              <tr>
                <th>Ürün</th>
                <th>Müşteri</th>
                <th>Adet</th>
                <th>Durum</th>
                <th>Tarih</th>
                <th>İşlem</th>
              </tr>
            </thead>
            <tbody>
              {preOrders.map((preOrder) => {
                const customerName = preOrder.customerName || preOrder.contact.name;
                const customerEmail = preOrder.customerEmail || preOrder.contact.email;
                const customerPhone = preOrder.contact.phone;

                return (
                <tr key={preOrder.id}>
                  <td>
                    <div className="admin-order-id-cell">
                      <Link to={getProductUrl(preOrder.productSlug)} target="_blank" rel="noreferrer">
                        {preOrder.productName}
                      </Link>
                      {preOrder.note && (
                        <span className="admin-table-note">{preOrder.note}</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="admin-order-customer">
                      <strong>{customerName}</strong>
                      <a href={`mailto:${customerEmail}`} className="admin-contact-link">
                        {customerEmail}
                      </a>
                      <a href={`tel:${customerPhone}`} className="admin-contact-link">
                        {customerPhone}
                      </a>
                    </div>
                  </td>
                  <td>{preOrder.quantity}</td>
                  <td>
                    <span className={`order-status status-${preOrder.status}`}>
                      {PRE_ORDER_STATUS_LABELS[preOrder.status]}
                    </span>
                  </td>
                  <td>{formatDate(preOrder.createdAt)}</td>
                  <td>
                    {preOrder.status === 'pending' ? (
                      <div className="admin-order-request-actions">
                        <textarea
                          rows={2}
                          placeholder="Admin notu (isteğe bağlı)"
                          value={adminNotes[preOrder.id] ?? ''}
                          disabled={updatingId === preOrder.id}
                          onChange={(e) =>
                            setAdminNotes((prev) => ({ ...prev, [preOrder.id]: e.target.value }))
                          }
                        />
                        <div className="admin-order-request-buttons">
                          <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            disabled={updatingId === preOrder.id}
                            onClick={() => void handleUpdate(preOrder.id, 'fulfilled')}
                          >
                            Tamamlandı
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline btn-sm"
                            disabled={updatingId === preOrder.id}
                            onClick={() => void handleUpdate(preOrder.id, 'cancelled')}
                          >
                            İptal
                          </button>
                        </div>
                      </div>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
